import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorBoundary } from './components';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import { StrayyosPage } from './pages/Strayyos';
import { AngelsPage } from './pages/Angels';
import { ResourcesPage } from './pages/Resources';
import { UsersPage, DeletionsPage } from './pages/Users';
import { AdoptionsPage } from './pages/Adoptions';
import { ReportsPage } from './pages/Reports';

// Loading component
const LoadingScreen: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-orange-600 mb-4">Strayyo</h1>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
        <p className="mt-4 text-sm text-gray-600">Loading...</p>
      </div>
    </div>
  </div>
);

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <DashboardPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Placeholder routes for future pages */}
        <Route
          path="/strayyos"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <StrayyosPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/angels"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <AngelsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/resources"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <ResourcesPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <UsersPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/deletions"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <DeletionsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/emergencies"
          element={
            <ProtectedRoute>
              <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-gray-900 mb-4">Emergency Management</h1>
                  <p className="text-gray-600">Coming soon...</p>
                  <a href="/" className="mt-4 inline-block text-orange-600 hover:text-orange-700">
                    ← Back to Dashboard
                  </a>
                </div>
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="/claims"
          element={
            <ProtectedRoute>
              <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-gray-900 mb-4">Claims Management</h1>
                  <p className="text-gray-600">Coming soon...</p>
                  <a href="/" className="mt-4 inline-block text-orange-600 hover:text-orange-700">
                    ← Back to Dashboard
                  </a>
                </div>
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <ReportsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/adoptions"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <AdoptionsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
